/**
 * ═══════════════════════════════════════════════════════════════
 * Performance Benchmarking — Critical Path Measurement
 * ═══════════════════════════════════════════════════════════════
 *
 * Tracks performance of critical user paths using the Performance API.
 * Measures: page load, navigation, query execution, and user interactions.
 * Results stored in sessionStorage and periodically flushed to DB.
 */
import { supabase } from '../supabase';

interface PerfEntry {
  name: string;
  category: 'page_load' | 'navigation' | 'query' | 'action' | 'render';
  duration_ms: number;
  path: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

// In-memory metrics buffer
const metricsBuffer: PerfEntry[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

// Thresholds — flag entries exceeding these as slow
const THRESHOLDS: Record<string, number> = {
  page_load: 3000,      // 3s page load
  navigation: 1000,     // 1s route change
  query: 2000,          // 2s Supabase query
  action: 5000,         // 5s user action (approve, save)
  render: 500,          // 500ms component render
};

/** Flush metrics to system_metrics table */
async function flushMetrics() {
  if (metricsBuffer.length === 0) return;
  const batch = metricsBuffer.splice(0, metricsBuffer.length);

  try {
    await supabase.from('system_metrics').insert(
      batch.map(m => ({
        metric_type: m.name,
        metric_value: m.duration_ms,
        unit: 'ms',
        tags: { category: m.category, path: m.path, ...m.metadata },
        recorded_at: m.timestamp,
      }))
    );
  } catch {
    // Non-blocking
  }
}

/**
 * Initialize performance monitoring.
 * Call once in main.tsx.
 */
export function initPerformanceMonitoring() {
  if (flushTimer) return;
  flushTimer = setInterval(flushMetrics, 30_000); // Flush every 30s

  // Track Web Vitals via PerformanceObserver
  if ('PerformanceObserver' in window) {
    // Largest Contentful Paint
    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        recordMetric('lcp', 'page_load', lastEntry.startTime);
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch {}

    // First Input Delay
    try {
      const fidObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const e = entry as PerformanceEventTiming;
          recordMetric('fid', 'action', e.processingStart - e.startTime);
        }
      });
      fidObserver.observe({ type: 'first-input', buffered: true });
    } catch {}

    // Long tasks (>50ms)
    try {
      const longTaskObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > 100) { // Only flag >100ms tasks
            recordMetric('long_task', 'render', entry.duration, {
              attribution: (entry as any).attribution?.[0]?.name,
            });
          }
        }
      });
      longTaskObserver.observe({ type: 'longtask', buffered: true });
    } catch {}
  }

  // Track route changes
  let lastNavTime = performance.now();
  const originalPushState = history.pushState;
  history.pushState = function (...args) {
    const now = performance.now();
    const duration = now - lastNavTime;
    if (duration < 30000) { // Ignore if >30s (user was idle)
      recordMetric('route_change', 'navigation', duration, {
        from: window.location.pathname,
        to: typeof args[2] === 'string' ? args[2] : '',
      });
    }
    lastNavTime = now;
    return originalPushState.apply(this, args);
  };

  // Flush on unload
  window.addEventListener('beforeunload', () => flushMetrics());
}

/**
 * Record a performance metric.
 * Automatically flags as "slow" if it exceeds the threshold.
 */
export function recordMetric(
  name: string,
  category: PerfEntry['category'],
  duration_ms: number,
  metadata?: Record<string, unknown>,
) {
  const entry: PerfEntry = {
    name,
    category,
    duration_ms: Math.round(duration_ms * 100) / 100,
    path: window.location.pathname,
    timestamp: new Date().toISOString(),
    metadata: {
      ...metadata,
      is_slow: duration_ms > (THRESHOLDS[category] ?? 5000),
    },
  };

  metricsBuffer.push(entry);

  // Store in sessionStorage for dashboard
  try {
    const stored = JSON.parse(sessionStorage.getItem('nizam_perf_feed') ?? '[]');
    stored.push({ name: entry.name, category: entry.category, ms: entry.duration_ms, path: entry.path, slow: entry.metadata?.is_slow, time: entry.timestamp });
    if (stored.length > 200) stored.splice(0, stored.length - 200);
    sessionStorage.setItem('nizam_perf_feed', JSON.stringify(stored));
  } catch {}

  // Console warning for slow operations
  if (duration_ms > (THRESHOLDS[category] ?? 5000)) {
    console.warn(`[Perf] SLOW ${category}: ${name} took ${duration_ms.toFixed(0)}ms (threshold: ${THRESHOLDS[category]}ms)`, metadata);
  }
}

/**
 * Time an async operation and record the metric.
 *
 * @example
 * const invoices = await benchmarkAsync('load_invoices', 'query', () => getInvoices(companyId));
 */
export async function benchmarkAsync<T>(
  name: string,
  category: PerfEntry['category'],
  fn: () => Promise<T>,
  metadata?: Record<string, unknown>,
): Promise<T> {
  const start = performance.now();
  try {
    const result = await fn();
    recordMetric(name, category, performance.now() - start, { ...metadata, status: 'success' });
    return result;
  } catch (err) {
    recordMetric(name, category, performance.now() - start, { ...metadata, status: 'error', error: (err as Error).message });
    throw err;
  }
}

/**
 * Time a synchronous operation.
 */
export function benchmarkSync<T>(
  name: string,
  category: PerfEntry['category'],
  fn: () => T,
  metadata?: Record<string, unknown>,
): T {
  const start = performance.now();
  const result = fn();
  recordMetric(name, category, performance.now() - start, metadata);
  return result;
}

/** Get recent performance metrics from sessionStorage */
export function getRecentMetrics(): Array<{ name: string; category: string; ms: number; path: string; slow: boolean; time: string }> {
  try {
    return JSON.parse(sessionStorage.getItem('nizam_perf_feed') ?? '[]');
  } catch {
    return [];
  }
}

/** Get summary stats */
export function getPerformanceSummary(): {
  avg_page_load_ms: number;
  avg_query_ms: number;
  slow_count: number;
  total_count: number;
} {
  const metrics = getRecentMetrics();
  const pageLoads = metrics.filter(m => m.category === 'page_load');
  const queries = metrics.filter(m => m.category === 'query');
  const slow = metrics.filter(m => m.slow);

  return {
    avg_page_load_ms: pageLoads.length > 0
      ? Math.round(pageLoads.reduce((s, m) => s + m.ms, 0) / pageLoads.length)
      : 0,
    avg_query_ms: queries.length > 0
      ? Math.round(queries.reduce((s, m) => s + m.ms, 0) / queries.length)
      : 0,
    slow_count: slow.length,
    total_count: metrics.length,
  };
}
